/**
 * L'onglet Démarrage : auditer le dossier racine, puis curer le résultat.
 *
 * Quand une affaire est née d'un dossier OneDrive, cet onglet en **lance
 * l'audit** — une flotte parcourt le dossier, analyse son contexte et **tranche**
 * la catégorie de chaque fichier — puis en **montre le rapport, éditable** : la
 * flotte propose, l'utilisateur dispose. Chaque carte de catégorie s'ouvre sur
 * un éditeur : renommer, annoter, déplacer un fichier vers une autre catégorie,
 * l'écarter, l'étiqueter.
 *
 * **L'audit constate, il n'importe rien.** Les pièces s'importent depuis l'onglet
 * Documents ; l'audit dresse l'état des lieux, que le métreur ajuste.
 */
import { useEffect, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import {
  CATEGORIES,
  type CategorieAudit,
  type CompteImport,
  type DiffAudit,
  type DossierRacine,
  type FichierAudit,
  type GrappeDoublons,
  type HistoriqueAudit,
  type RapportAudit,
  type RapportDoublons,
  type SuggestionNote,
  type Travail,
} from "../../api/types";
import { Avis } from "../../composants/avis";
import { useModale } from "../../composants/clavier";
import {
  TRAVAIL_EN_COURS,
  useDonnees,
  useSuivreLaFile,
  type Veille,
} from "../../composants/donnees";
import { Chargement, Vide } from "../../composants/etats";
import { poids, quand } from "../../composants/formats";

export function Demarrage({
  affaireId,
  racine,
  etat,
  veille,
}: {
  affaireId: string;
  racine: DossierRacine;
  etat: string;
  veille: Veille<Travail>;
}) {
  // Le rapport consulté : le courant (null), ou un rapport passé de l'historique.
  const [vu, poserVu] = useState<string | null>(null);
  const rapport = useDonnees<RapportAudit | null>(
    () => api.lire(`/api/v1/affaires/${affaireId}/audit`
      + (vu ? `?rapport=${encodeURIComponent(vu)}` : "")),
    [affaireId, vu],
  );
  // La veille ne rafraîchit que la vue courante : un rapport passé ne bouge plus.
  useSuivreLaFile(veille.empreinte, () => { if (!vu) rapport.recharger(); });

  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");

  const donnees = rapport.donnees ?? null;
  const enCours = donnees != null && TRAVAIL_EN_COURS.has(donnees.etat);
  const pret = etat === "READY";

  async function lancer() {
    poserEnvoi(true);
    poserErreur("");
    try {
      await api.ecrire(`/api/v1/affaires/${affaireId}/audit`);
      poserVu(null);            // on repasse sur l'audit courant
      veille.rafraichir();
      rapport.recharger();
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi
          ? exc.message
          : "L'audit n'a pas pu être lancé. Réessayez.",
      );
    } finally {
      poserEnvoi(false);
    }
  }

  if (rapport.charge && !donnees) {
    return <div className="contenu"><Chargement lignes={3} /></div>;
  }

  return (
    <div className="contenu pile">
      <div className="carte">
        <div className="carte__corps pile">
          <div className="rangee rangee--espacee">
            <div className="pile pile--serree" style={{ minWidth: 0 }}>
              <h2>Audit du dossier racine</h2>
              <span className="discret tronque">
                <span aria-hidden="true">📁 </span>
                <span className="mono">{racine.chemin || racine.nom}</span>
              </span>
            </div>
            <button
              type="button"
              className="bouton bouton--primaire"
              disabled={envoi || enCours || !pret}
              onClick={lancer}
            >
              {enCours
                ? "Audit en cours…"
                : envoi
                  ? "Lancement…"
                  : donnees
                    ? "Relancer l'audit"
                    : "Lancer l'audit"}
            </button>
          </div>

          {!pret && (
            <div className="avis avis--info">
              L'affaire finit de se préparer ; l'audit sera possible dès qu'elle
              est prête.
            </div>
          )}
          {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}

          <span className="discret">
            La flotte parcourt le dossier, tranche la catégorie de chaque fichier,
            et vous laisse corriger. Elle <strong>ne dépose rien</strong> : les
            pièces s'importent depuis l'onglet Documents.
          </span>
        </div>
      </div>

      {donnees && donnees.historique.length > 1 && (
        <Historique
          entrees={donnees.historique}
          actif={donnees.report_id}
          choisir={(id) =>
            poserVu(id === donnees.historique[0]?.report_id ? null : id)}
        />
      )}

      {donnees && !donnees.courant && (
        <DiffPanel diff={donnees.diff} date={quand(donnees.commence_le)}
                   retour={() => poserVu(null)} />
      )}

      {donnees == null ? (
        <Vide
          titre="Aucun audit encore lancé"
          aide="Lancez l'audit pour savoir ce que le dossier racine contient, et
                ce qui manque avant de chiffrer."
        />
      ) : donnees.etat === "FAILED" ? (
        <Avis ton="erreur" cle={donnees.motif}>
          L'audit s'est interrompu{donnees.motif ? ` (${donnees.motif})` : ""}.
          {donnees.detail ? ` ${donnees.detail}` : ""} Relancez-le une fois la
          cause levée.
        </Avis>
      ) : enCours ? (
        <div className="carte">
          <div className="carte__corps pile">
            <Chargement lignes={1} />
            <span className="discret">
              {donnees.dossiers_vus} dossier(s) parcouru(s),
              {" "}{donnees.fichiers_vus} fichier(s) vus,
              {" "}{donnees.classes} classé(s)…
            </span>
          </div>
        </div>
      ) : (
        <RapportVue
          rapport={donnees}
          affaireId={affaireId}
          recharger={rapport.recharger}
          lectureSeule={!donnees.courant}
        />
      )}
    </div>
  );
}

/** La frise des audits passés, du plus récent au plus ancien. */
function Historique({
  entrees,
  actif,
  choisir,
}: {
  entrees: HistoriqueAudit[];
  actif: string;
  choisir: (reportId: string) => void;
}) {
  return (
    <div className="pile pile--serree">
      <span className="discret">Historique des audits</span>
      <div className="demarrage__frise">
        {entrees.map((e, rang) => (
          <button
            key={e.report_id}
            type="button"
            className={`demarrage__jalon${e.report_id === actif ? " demarrage__jalon--actif" : ""}`}
            aria-pressed={e.report_id === actif}
            onClick={() => choisir(e.report_id)}
          >
            <span className={`demarrage__pastille demarrage__pastille--${
              e.etat === "READY" ? (e.complet ? "ok" : "manque") : "cours"}`}
                  aria-hidden="true" />
            <span className="pile pile--serree" style={{ minWidth: 0 }}>
              <strong>{quand(e.commence_le)}</strong>
              <span className="discret">
                {rang === 0 && "courant · "}{e.fichiers_vus} fichier(s)
                {e.etat === "READY" && !e.complet && " · incomplet"}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Ce qui a changé d'un rapport passé au courant : compteurs, écart par
 *  catégorie, et les listes des apparus / disparus / déplacés. */
function DiffPanel({
  diff,
  date,
  retour,
}: {
  diff: DiffAudit | null;
  date: string;
  retour: () => void;
}) {
  return (
    <div className="carte demarrage__diff">
      <div className="carte__corps pile">
        <div className="rangee rangee--espacee">
          <div className="pile pile--serree" style={{ minWidth: 0 }}>
            <h3>Audit du {date}</h3>
            <span className="discret">Lecture seule — comparé à l'audit courant.</span>
          </div>
          <button type="button" className="bouton bouton--discret" onClick={retour}>
            Revenir à l'audit courant
          </button>
        </div>

        {!diff ? (
          <span className="discret">Rien à comparer.</span>
        ) : (
          <>
            <div className="demarrage__diff-chiffres">
              <span className="demarrage__delta demarrage__delta--plus">
                +{diff.resume.nouveaux} apparu(s)
              </span>
              <span className="demarrage__delta demarrage__delta--moins">
                −{diff.resume.disparus} disparu(s)
              </span>
              <span className="demarrage__delta">
                {diff.resume.deplaces} déplacé(s)
              </span>
              <span className="discret">{diff.resume.inchanges} inchangé(s)</span>
            </div>

            {diff.par_categorie.length > 0 && (
              <ul className="demarrage__diff-cats">
                {diff.par_categorie.map((c) => (
                  <li key={c.clef} className="rangee rangee--espacee">
                    <span className="tronque">{c.libelle}</span>
                    <span className="nombre">
                      {c.avant} → {c.apres}
                      {c.delta !== 0 && (
                        <span className={c.delta > 0
                          ? "demarrage__delta--plus" : "demarrage__delta--moins"}>
                          {" "}({c.delta > 0 ? "+" : ""}{c.delta})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {diff.deplaces.length > 0 && (
              <details>
                <summary>{diff.deplaces.length} fichier(s) déplacé(s)</summary>
                <ul className="demarrage__diff-liste">
                  {diff.deplaces.map((f) => (
                    <li key={f.chemin} className="tronque" title={f.chemin}>
                      {f.nom}{" "}
                      <span className="discret">{f.de} → {f.vers}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {diff.nouveaux.length > 0 && (
              <details>
                <summary>{diff.nouveaux.length} apparu(s) depuis</summary>
                <ul className="demarrage__diff-liste">
                  {diff.nouveaux.map((f) => (
                    <li key={f.chemin} className="tronque" title={f.chemin}>
                      <span className="demarrage__delta--plus">＋</span> {f.nom}{" "}
                      <span className="discret">{f.categorie}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {diff.disparus.length > 0 && (
              <details>
                <summary>{diff.disparus.length} disparu(s) depuis</summary>
                <ul className="demarrage__diff-liste">
                  {diff.disparus.map((f) => (
                    <li key={f.chemin} className="tronque" title={f.chemin}>
                      <span className="demarrage__delta--moins">－</span> {f.nom}{" "}
                      <span className="discret">{f.categorie}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Le résultat d'une recherche de doublons pour une carte, conservé le temps de
 *  la session d'édition : le rapport rendu et les fichiers déjà tranchés. */
type EtatDoublons = { rapport: RapportDoublons; traites: string[] };

/** Ce qu'on garde dans le navigateur : les recherches de doublons d'une affaire,
 *  par carte, rattachées à l'audit courant. Rangé par `report_id` : un nouvel
 *  audit change les identifiants de fichiers, l'ancien résultat n'a plus cours. */
type StockDoublons = {
  affaireId: string;
  reportId: string;
  parClef: Record<string, EtatDoublons>;
};

const CLE_DOUBLONS = "metre.doublons.v1";

/** Relit le stock de doublons d'une affaire. Tout accès est enveloppé : en
 *  navigation privée, quota plein, ou stockage coupé, la lecture rend le vide
 *  plutôt que de jeter — la persistance est un confort, pas un dû. */
function lireDoublons(affaireId: string): StockDoublons {
  const vide: StockDoublons = { affaireId, reportId: "", parClef: {} };
  try {
    const brut = localStorage.getItem(`${CLE_DOUBLONS}.${affaireId}`);
    if (!brut) return vide;
    const val = JSON.parse(brut) as StockDoublons;
    return val && val.affaireId === affaireId ? val : vide;
  } catch {
    return vide;
  }
}

/** Écrit le stock (ou l'efface s'il est vide). Silencieux si le stockage refuse. */
function ecrireDoublons(stock: StockDoublons): void {
  try {
    const clef = `${CLE_DOUBLONS}.${stock.affaireId}`;
    if (Object.keys(stock.parClef).length === 0) localStorage.removeItem(clef);
    else localStorage.setItem(clef, JSON.stringify(stock));
  } catch {
    /* stockage indisponible : sans effet, l'application marche sans lui */
  }
}

/** Le rapport achevé : cartes de catégories éditables, manque, écartés. */
function RapportVue({
  rapport,
  affaireId,
  recharger,
  lectureSeule,
}: {
  rapport: RapportAudit;
  affaireId: string;
  recharger: () => void;
  lectureSeule: boolean;
}) {
  // La carte en cours d'édition, désignée par sa clef ; relue à chaque rendu
  // depuis le rapport frais, pour que l'éditeur suive les changements.
  const [edite, poserEdite] = useState<string | null>(null);
  const carteEditee = rapport.categories.find((c) => c.clef === edite) ?? null;

  // Le résultat « Chercher les doublons » vit ici, non dans l'éditeur : il
  // survit ainsi à la fermeture d'une carte, ET au rafraîchissement du
  // navigateur (rangé en `localStorage`). On l'oublie dès qu'un nouvel audit
  // devient courant — les identifiants de fichiers changent, un ancien verdict
  // n'aurait plus de sens.
  const [stock, poserStock] = useState<StockDoublons>(() => lireDoublons(affaireId));
  // Relire au changement d'affaire (l'instance de la page peut être réutilisée).
  useEffect(() => { poserStock(lireDoublons(affaireId)); }, [affaireId]);
  // Persister, jamais sous une autre affaire qu'un changement en cours aurait
  // laissée en mémoire un instant.
  useEffect(() => {
    if (stock.affaireId === affaireId) ecrireDoublons(stock);
  }, [affaireId, stock]);

  const doublons = stock.reportId === rapport.report_id ? stock.parClef : {};
  const majDoublons = (
    clef: string, f: (prec?: EtatDoublons) => EtatDoublons | undefined,
  ) =>
    poserStock((s) => {
      const base = s.reportId === rapport.report_id ? s.parClef : {};
      const suivant = f(base[clef]);
      const parClef = { ...base };
      if (suivant) parClef[clef] = suivant;
      else delete parClef[clef];
      return { affaireId, reportId: rapport.report_id, parClef };
    });

  const patch = async (id: string, champs: Record<string, unknown>) => {
    await api.modifier(
      `/api/v1/affaires/${affaireId}/audit/findings/${id}`, champs);
    recharger();
  };

  // **À confirmer, à travers les cartes.** Les fichiers que la flotte a tranchés
  // par déduction (confiance faible) et que l'utilisateur n'a pas encore avalisés
  // (pas de catégorie retenue). Poser une catégorie retenue — même la même —
  // vaut confirmation, et le fichier quitte cette liste. Les hors-périmètre ont
  // déjà leur propre carte.
  const aConfirmer = rapport.categories
    .filter((c) => c.clef !== "AUTRE")
    .flatMap((c) =>
      c.fichiers
        .filter((f) => f.confiance === "faible" && !f.categorie_retenue)
        .map((f) => ({ f, clef: c.clef })));

  return (
    <>
      {rapport.manque.length > 0 && (
        <Avis ton="attention" cle={rapport.manque.map((m) => m.titre).join("|")}>
          <div className="pile pile--serree">
            <strong>Avant de pouvoir chiffrer, il manque :</strong>
            {rapport.manque.map((m) => (
              <span key={m.titre}>
                <strong>{m.titre}.</strong>{" "}
                <span className="discret">{m.raison}</span>
              </span>
            ))}
          </div>
        </Avis>
      )}

      {!lectureSeule && aConfirmer.length > 0 && (
        <details className="carte demarrage__revue">
          <summary>
            {aConfirmer.length} fichier(s) à confirmer
            <span className="discret"> — tranchés par déduction</span>
          </summary>
          <div className="pile pile--serree"
               style={{ marginTop: "var(--mo-space-3)" }}>
            <span className="discret">
              Confirmez la catégorie, déplacez le fichier, ou écartez-le.
            </span>
            {aConfirmer.map(({ f, clef }) => (
              <div key={f.id} className="rangee demarrage__ambigu">
                <span aria-hidden="true">📄</span>
                <span className="tronque" style={{ flex: 1 }} title={f.motif}>
                  {f.nom}
                </span>
                <select aria-label={`Catégorie de ${f.nom}`} value={clef}
                        onChange={(e) => void patch(f.id,
                          { categorie_retenue: e.target.value })}>
                  {CATEGORIES.map((c) => (
                    <option key={c.clef} value={c.clef}>{c.libelle}</option>
                  ))}
                </select>
                <button type="button"
                        className="bouton bouton--discret selecteur__choisir"
                        onClick={() => void patch(f.id,
                          { categorie_retenue: clef })}>
                  Confirmer
                </button>
                <button type="button" className="action" title="Écarter"
                        aria-label={`Écarter ${f.nom}`}
                        onClick={() => void patch(f.id, { retire: true })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                    <path d="m6 6 12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="demarrage__grille">
        {rapport.categories.map((carte) => (
          <CarteCategorie key={carte.clef} carte={carte}
                          lectureSeule={lectureSeule}
                          ouvrir={() => poserEdite(carte.clef)} />
        ))}
      </div>

      {rapport.ecartes.length > 0 && (
        <details className="carte demarrage__ecartes">
          <summary>{rapport.ecartes.length} fichier(s) écarté(s)</summary>
          <div className="pile pile--serree" style={{ marginTop: "var(--mo-space-3)" }}>
            {rapport.ecartes.map((f) => (
              <div key={f.id} className="rangee demarrage__ambigu">
                <span aria-hidden="true">📄</span>
                <span className="tronque" style={{ flex: 1 }}>{f.nom}</span>
                {!lectureSeule && (
                  <button type="button"
                          className="bouton bouton--discret selecteur__choisir"
                          onClick={() => void patch(f.id, { retire: false })}>
                    Remettre
                  </button>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      <span className="discret">
        {rapport.fichiers_vus} fichier(s) dans {rapport.dossiers_vus} dossier(s)
        {rapport.tronque && " · dossier trop grand : l'audit n'a pas tout vu"}
      </span>

      {carteEditee && (
        <EditeurCategorie
          carte={carteEditee}
          affaireId={affaireId}
          recharger={recharger}
          fermer={() => poserEdite(null)}
          doublons={doublons[carteEditee.clef]}
          majDoublons={majDoublons}
        />
      )}
    </>
  );
}

/** Une carte de catégorie : son nom, son compte, un aperçu, et « Modifier ». */
function CarteCategorie({
  carte,
  ouvrir,
  lectureSeule,
}: {
  carte: CategorieAudit;
  ouvrir: () => void;
  lectureSeule: boolean;
}) {
  const nom = carte.nom || carte.libelle;
  const apercu = carte.fichiers.slice(0, 5);
  const aConfirmer = carte.fichiers.filter(
    (f) => f.confiance === "faible" && !f.categorie_retenue).length;
  // Vert quand tout est importé, rouge quand il reste à importer ; le
  // hors-périmètre (AUTRE), lui, n'est pas importable — il garde l'accent.
  const importable = carte.clef !== "AUTRE" && carte.presents > 0;
  const toutImporte = importable && carte.importes === carte.presents;
  const modif = carte.presents === 0 ? ""
    : !importable ? " demarrage__mesure--pleine"
      : toutImporte ? " demarrage__mesure--importee"
        : " demarrage__mesure--aimporter";
  return (
    <div className={`demarrage__mesure${modif}`}>
      <div className="rangee rangee--espacee">
        <strong className="tronque">{nom}</strong>
        <span className="rangee" style={{ gap: "var(--mo-space-2)" }}>
          <span className="demarrage__compte">{carte.presents}</span>
          {!lectureSeule && (
            <button type="button" className="action" title={`Modifier « ${nom} »`}
                    aria-label={`Modifier la catégorie ${nom}`} onClick={ouvrir}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </button>
          )}
        </span>
      </div>
      {importable && (
        <span className={`demarrage__importbadge${
          toutImporte ? " demarrage__importbadge--ok" : ""}`}>
          {toutImporte ? "✓ importé" : `${carte.importes}/${carte.presents} importés`}
        </span>
      )}
      {aConfirmer > 0 && (
        <span className="demarrage__aconf">{aConfirmer} à confirmer</span>
      )}
      {carte.commentaire && (
        <span className="discret">{carte.commentaire}</span>
      )}
      {carte.etiquettes.length > 0 && (
        <div className="demarrage__tags">
          {carte.etiquettes.map((e) => (
            <span key={e} className="demarrage__tag">{e}</span>
          ))}
        </div>
      )}
      {apercu.length > 0 && (
        <ul className="demarrage__exemples">
          {apercu.map((f) => (
            <li key={f.id} className="tronque" title={f.motif}>
              {f.nom}
              {f.confiance === "faible" && !f.categorie_retenue && (
                <span className="discret"> · à confirmer</span>
              )}
            </li>
          ))}
          {carte.presents > apercu.length && (
            <li className="discret">… et {carte.presents - apercu.length} de plus</li>
          )}
        </ul>
      )}
    </div>
  );
}

/** L'éditeur d'une carte : nom, notes, et le sort de chaque fichier. */
function EditeurCategorie({
  carte,
  affaireId,
  recharger,
  fermer,
  doublons,
  majDoublons,
}: {
  carte: CategorieAudit;
  affaireId: string;
  recharger: () => void;
  fermer: () => void;
  doublons: EtatDoublons | undefined;
  majDoublons: (
    clef: string, f: (prec?: EtatDoublons) => EtatDoublons | undefined,
  ) => void;
}) {
  const cadre = useModale<HTMLDivElement>(true, fermer);
  const [nom, poserNom] = useState(carte.nom);
  const [commentaire, poserCommentaire] = useState(carte.commentaire);
  const [etiquettes, poserEtiquettes] = useState<string[]>(carte.etiquettes);
  const [enreg, poserEnreg] = useState(false);

  // On resynchronise les champs de note quand on ouvre une AUTRE carte, non à
  // chaque recomposition : sinon un rechargement (retrait de fichier, doublons)
  // effacerait une note en cours de saisie non encore enregistrée.
  useEffect(() => {
    poserNom(carte.nom);
    poserCommentaire(carte.commentaire);
    poserEtiquettes(carte.etiquettes);
  }, [carte.clef]);  // eslint-disable-line react-hooks/exhaustive-deps

  const base = `/api/v1/affaires/${affaireId}/audit`;

  async function patchFichier(id: string, champs: Record<string, unknown>) {
    await api.modifier(`${base}/findings/${id}`, champs);
    recharger();
  }

  // Retirer plusieurs fichiers d'un coup (« Tout accepter » des doublons) : une
  // seule recomposition à la fin, plutôt qu'une par fichier.
  async function retirerLot(ids: string[]) {
    await Promise.all(ids.map((id) =>
      api.modifier(`${base}/findings/${id}`, { retire: true })));
    recharger();
  }

  async function enregistrerNotes() {
    poserEnreg(true);
    try {
      await api.modifier(`${base}/categories/${carte.clef}`,
        { nom, commentaire, etiquettes });
      recharger();
    } finally {
      poserEnreg(false);
    }
  }

  // ── Import vers l'onglet Documents ────────────────────────────────────────
  const [selection, poserSelection] = useState<Set<string>>(() => new Set());
  const [importe, poserImporte] = useState(false);
  const [compte, poserCompte] = useState<CompteImport | null>(null);
  const importable = carte.clef !== "AUTRE";
  // Sélection et compte rendu remis à zéro quand on ouvre une autre carte.
  useEffect(() => { poserSelection(new Set()); poserCompte(null); },
            [carte.clef]);  // eslint-disable-line react-hooks/exhaustive-deps

  const basculer = (id: string) =>
    poserSelection((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Ce qui reste à importer (ni déjà importé, ni en cours de descente).
  const aImporter = carte.fichiers
    .filter((f) => f.importe !== "PRET" && f.importe !== "EN_COURS")
    .map((f) => f.id);

  async function lancerImport(ids: string[]) {
    if (ids.length === 0) return;
    poserImporte(true);
    poserCompte(null);
    try {
      const c = await api.ecrire<CompteImport>(`${base}/import`, { findings: ids });
      poserCompte(c);
      poserSelection(new Set());
      recharger();
    } catch (exc) {
      poserCompte({
        demandes: ids.length, deposes: 0, deja: 0,
        refus: [{ nom: "", raison: exc instanceof ErreurApi
          ? exc.message : "L'import n'a pas pu être lancé." }],
      });
    } finally {
      poserImporte(false);
    }
  }

  const nomAffiche = carte.nom || carte.libelle;

  return (
    <div className="voile" role="dialog" aria-modal="true"
         aria-label={`Modifier ${nomAffiche}`}>
      <div className="modale modale--picker" ref={cadre}>
        <div className="modale__tete">
          <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
            <h2>Modifier « {nomAffiche} »</h2>
            <span className="discret">
              {carte.presents} fichier(s). Renommez la carte, annotez-la, ou
              déplacez ce qui n'est pas à sa place.
            </span>
          </div>
        </div>

        <div className="modale__corps pile">
          {carte.clef !== "AUTRE" && (
            <div className="pile pile--serree editeur__notes">
              <div className="champ">
                <div className="rangee rangee--espacee">
                  <label htmlFor="cat-nom">Nom de la carte</label>
                  <BoutonSuggerer titre="Proposer un nom par l'IA"
                    url={`${base}/categories/${carte.clef}/suggestion?champ=nom`}
                    onSuggestion={(s) => { if (s.nom) poserNom(s.nom); }} />
                </div>
                <input id="cat-nom" value={nom}
                       placeholder={carte.libelle}
                       onChange={(e) => poserNom(e.target.value)} />
              </div>
              <div className="champ">
                <div className="rangee rangee--espacee">
                  <label htmlFor="cat-tags">Étiquettes</label>
                  <BoutonSuggerer titre="Proposer des étiquettes par l'IA"
                    url={`${base}/categories/${carte.clef}/suggestion?champ=etiquettes`}
                    onSuggestion={(s) => s.etiquettes
                      && poserEtiquettes((v) => fusionnerTags(v, s.etiquettes!))} />
                </div>
                <SaisieEtiquettes valeur={etiquettes} poser={poserEtiquettes}
                                  placeholder="lot 04, à vérifier"
                                  ariaLabel="Étiquettes de la carte" />
              </div>
              <div className="champ">
                <div className="rangee rangee--espacee">
                  <label htmlFor="cat-com">Commentaire</label>
                  <BoutonSuggerer titre="Proposer un commentaire par l'IA"
                    url={`${base}/categories/${carte.clef}/suggestion?champ=commentaire`}
                    onSuggestion={(s) => { if (s.commentaire) poserCommentaire(s.commentaire); }} />
                </div>
                <textarea id="cat-com" rows={2} value={commentaire}
                          onChange={(e) => poserCommentaire(e.target.value)} />
              </div>
              <div>
                <button type="button" className="bouton" disabled={enreg}
                        onClick={enregistrerNotes}>
                  {enreg ? "Enregistrement…" : "Enregistrer les notes"}
                </button>
              </div>
            </div>
          )}

          {carte.fichiers.length >= 2 && (
            <DoublonsSection affaireId={affaireId} categorie={carte.clef}
                             patch={patchFichier} retirerLot={retirerLot}
                             etat={doublons}
                             poserEtat={(f) => majDoublons(carte.clef, f)} />
          )}

          {importable && carte.fichiers.length > 0 && (
            <div className="rangee rangee--espacee demarrage__barreimport">
              <span className="discret">
                {carte.importes}/{carte.presents} importé(s) dans Documents
              </span>
              <span className="rangee" style={{ gap: "var(--mo-space-2)" }}>
                {selection.size > 0 && (
                  <button type="button" className="bouton bouton--discret"
                          disabled={importe}
                          onClick={() => void lancerImport([...selection])}>
                    Importer la sélection ({selection.size})
                  </button>
                )}
                <button type="button" className="bouton bouton--primaire"
                        disabled={importe || aImporter.length === 0}
                        onClick={() => void lancerImport(aImporter)}>
                  {importe ? "Import…"
                    : aImporter.length === 0 ? "Tout importé ✓"
                      : `Tout importer (${aImporter.length})`}
                </button>
              </span>
            </div>
          )}

          {compte && (compte.deposes > 0 || compte.deja > 0
                      || compte.refus.length > 0) && (
            <Avis ton={compte.refus.length > 0 ? "attention" : "info"}
                  cle={`import-${compte.deposes}-${compte.deja}-${compte.refus.length}`}>
              <div className="pile pile--serree">
                {(compte.deposes > 0 || compte.deja > 0) && (
                  <span>
                    Import lancé : {compte.deposes} nouveau(x)
                    {compte.deja > 0 && `, ${compte.deja} déjà à jour`}. La
                    descente se fait en arrière-plan — le vert s'allume quand
                    c'est prêt.
                  </span>
                )}
                {compte.refus.length > 0 && (
                  <span>
                    <strong>{compte.refus.length} non importé(s) :</strong>{" "}
                    {compte.refus.map((r) => r.nom
                      ? `${r.nom} (${r.raison})` : r.raison).join(" ; ")}
                  </span>
                )}
              </div>
            </Avis>
          )}

          <div className="pile pile--serree">
            {carte.fichiers.length === 0 && (
              <Vide titre="Aucun fichier dans cette carte" />
            )}
            {carte.fichiers.map((f) => (
              <FichierLigne key={f.id} fichier={f} carteClef={carte.clef}
                            affaireId={affaireId} patch={patchFichier}
                            selectionnee={selection.has(f.id)}
                            basculer={importable ? () => basculer(f.id) : undefined}
                            importer={importable
                              ? () => lancerImport([f.id]) : undefined} />
            ))}
          </div>
        </div>

        <div className="modale__pied">
          <span className="discret" style={{ flex: 1 }}>
            Les changements sont enregistrés au fil de l'eau.
          </span>
          <button type="button" className="bouton bouton--primaire" onClick={fermer}>
            Terminé
          </button>
        </div>
      </div>
    </div>
  );
}

/** Repérer les doublons d'une carte : la flotte propose, l'utilisateur retire
 *  ou garde. Le retrait passe par la curation ordinaire (`retire`) ; « garder »
 *  n'écarte que la suggestion. La recherche est à la demande — elle consomme le
 *  modèle, on ne la lance pas à l'ouverture. */
function DoublonsSection({
  affaireId,
  categorie,
  patch,
  retirerLot,
  etat,
  poserEtat,
}: {
  affaireId: string;
  categorie: string;
  patch: (id: string, champs: Record<string, unknown>) => Promise<void>;
  retirerLot: (ids: string[]) => Promise<void>;
  etat: EtatDoublons | undefined;
  poserEtat: (f: (prec?: EtatDoublons) => EtatDoublons | undefined) => void;
}) {
  // La recherche (`etat`) est conservée par le parent, pour survivre à la
  // fermeture de la carte ; seuls le chargement et l'erreur restent locaux.
  const [charge, poserCharge] = useState(false);
  const [occupe, poserOccupe] = useState(false);
  const [erreur, poserErreur] = useState("");

  async function chercher() {
    poserCharge(true);
    poserErreur("");
    try {
      const issue = await api.lire<RapportDoublons>(
        `/api/v1/affaires/${affaireId}/audit/categories/${categorie}/doublons`);
      poserEtat(() => ({ rapport: issue, traites: [] }));
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi
          ? exc.message
          : "La recherche de doublons a échoué. Réessayez.");
    } finally {
      poserCharge(false);
    }
  }

  async function retirer(id: string) {
    await patch(id, { retire: true });
    poserEtat((p) => (p ? { ...p, traites: [...p.traites, id] } : p));
  }
  function garder(id: string) {
    poserEtat((p) => (p ? { ...p, traites: [...p.traites, id] } : p));
  }

  const rapport = etat?.rapport ?? null;
  const traites = new Set(etat?.traites ?? []);
  const visibles = (rapport?.grappes ?? [])
    .map((g) => ({ ...g, fichiers: g.fichiers.filter((f) => !traites.has(f.id)) }))
    .filter((g) => g.fichiers.length >= 2 && g.fichiers.some((f) => !f.garder));
  const aRetirer = visibles.flatMap((g) =>
    g.fichiers.filter((f) => !f.garder).map((f) => f.id));

  async function accepterTout() {
    if (aRetirer.length === 0) return;
    poserOccupe(true);
    try {
      await retirerLot(aRetirer);
      poserEtat((p) => (p ? { ...p, traites: [...p.traites, ...aRetirer] } : p));
    } finally {
      poserOccupe(false);
    }
  }

  return (
    <div className="pile pile--serree demarrage__doublons">
      <div className="rangee rangee--espacee">
        <span className="discret">Repérer les fichiers en double dans cette carte.</span>
        <button type="button" className="bouton bouton--discret"
                disabled={charge} onClick={chercher}>
          {charge
            ? "Recherche…"
            : rapport
              ? "Relancer la recherche"
              : "Chercher les doublons"}
        </button>
      </div>

      {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}

      {rapport && !charge && (
        visibles.length === 0 ? (
          <span className="discret">
            Aucun doublon repéré{rapport.tronque
              ? " (analyse partielle : beaucoup de fichiers)" : ""}.
          </span>
        ) : (
          <div className="pile pile--serree">
            {rapport.tronque && (
              <span className="discret">
                Beaucoup de fichiers : l'analyse a porté sur les groupes les plus
                nombreux.
              </span>
            )}
            <div className="rangee rangee--espacee">
              <span className="discret">
                {aRetirer.length} fichier(s) proposé(s) au retrait.
              </span>
              <button type="button"
                      className="bouton bouton--discret selecteur__choisir"
                      disabled={occupe} onClick={accepterTout}>
                {occupe ? "Retrait…" : `Tout accepter (${aRetirer.length})`}
              </button>
            </div>
            {visibles.map((g) => (
              <GrappeVue key={g.fichiers.find((f) => f.garder)?.id ?? g.fichiers[0]!.id}
                         grappe={g} retirer={retirer} garder={garder} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

/** Une grappe de doublons : le fichier gardé, et ceux proposés au retrait. */
function GrappeVue({
  grappe,
  retirer,
  garder,
}: {
  grappe: GrappeDoublons;
  retirer: (id: string) => Promise<void>;
  garder: (id: string) => void;
}) {
  return (
    <div className="demarrage__grappe pile pile--serree">
      <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
        <span className="demarrage__grappe-type">
          {grappe.type === "revision" ? "Révisions" : "Doublon"}
        </span>
        <span className="discret tronque">{grappe.motif}</span>
      </div>
      {grappe.fichiers.map((f) => (
        <div key={f.id} className="rangee demarrage__ambigu">
          <span aria-hidden="true">{f.garder ? "✅" : "📄"}</span>
          <span className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
            <span className="tronque" title={f.chemin}>{f.nom}</span>
            <span className="discret">
              {poids(f.octets)} · {quand(f.modifie_le)}
              {f.raison ? ` · ${f.raison}` : ""}
            </span>
          </span>
          {f.garder ? (
            <span className="demarrage__tag">À conserver</span>
          ) : (
            <>
              <button type="button" className="bouton bouton--discret"
                      onClick={() => garder(f.id)}>
                Garder
              </button>
              <button type="button"
                      className="bouton bouton--discret selecteur__choisir"
                      onClick={() => void retirer(f.id)}>
                Retirer
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/** Un champ d'étiquettes moderne : des puces qu'on ajoute (Entrée ou virgule) et
 *  qu'on retire d'un clic. `poser` reçoit la liste à jour à chaque changement. */
function SaisieEtiquettes({
  valeur,
  poser,
  placeholder,
  ariaLabel,
}: {
  valeur: string[];
  poser: (tags: string[]) => void;
  placeholder?: string;
  ariaLabel: string;
}) {
  const [saisie, poserSaisie] = useState("");

  const ajouter = (brut: string) => {
    const tokens = brut.split(",").map((t) => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;
    const fusion = [...valeur];
    for (const t of tokens) {
      if (!fusion.some((x) => x.toLowerCase() === t.toLowerCase())) fusion.push(t);
    }
    poser(fusion);
    poserSaisie("");
  };
  const retirer = (t: string) => poser(valeur.filter((x) => x !== t));

  return (
    <div className="etiq" onClick={(e) =>
      (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus()}>
      {valeur.map((t) => (
        <span key={t} className="etiq__puce">
          {t}
          <button type="button" className="etiq__x" aria-label={`Retirer ${t}`}
                  onClick={() => retirer(t)}>×</button>
        </span>
      ))}
      <input className="etiq__saisie" value={saisie}
             placeholder={valeur.length === 0 ? placeholder : ""}
             aria-label={ariaLabel}
             onChange={(e) => poserSaisie(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === "Enter" || e.key === ",") {
                 e.preventDefault();
                 ajouter(saisie);
               } else if (e.key === "Backspace" && !saisie && valeur.length > 0) {
                 retirer(valeur[valeur.length - 1]!);
               }
             }}
             onBlur={() => ajouter(saisie)} />
    </div>
  );
}

/** Un bouton « ✨ Suggérer » : interroge le modèle pour préremplir un champ. La
 *  suggestion est best-effort — un échec est silencieux, le champ reste à saisir. */
function BoutonSuggerer({
  url,
  onSuggestion,
  titre,
}: {
  url: string;
  onSuggestion: (s: SuggestionNote) => void;
  titre: string;
}) {
  const [charge, poserCharge] = useState(false);
  return (
    <button type="button" className="bouton bouton--discret editeur__ia"
            disabled={charge} title={titre} aria-label={titre}
            onClick={async () => {
              poserCharge(true);
              try {
                onSuggestion(await api.lire<SuggestionNote>(url));
              } catch {
                /* suggestion best-effort : sans effet en cas d'échec */
              } finally {
                poserCharge(false);
              }
            }}>
      <span aria-hidden="true">✨</span> {charge ? "…" : "Suggérer"}
    </button>
  );
}

/** Fusionne des étiquettes proposées avec l'existant, sans doublon (casse ignorée). */
function fusionnerTags(actuel: string[], ajout: string[]): string[] {
  const sortie = [...actuel];
  for (const t of ajout) {
    if (t && !sortie.some((x) => x.toLowerCase() === t.toLowerCase())) sortie.push(t);
  }
  return sortie;
}

//: Au-delà, l'aperçu n'est pas relayé (aligné sur APERCU_MAX_OCTETS au serveur).
const APERCU_MAX_OCTETS = 50 * 1024 * 1024;
//: Ce qui s'affiche en ligne : PDF/images (natif) et tableurs (rendus en
//: tableau HTML par la façade). Le reste (.xls hérité, .dwg, .ifc) s'ouvre dans
//: un onglet.
const TYPES_APERCU = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp",
  ".xlsx", ".xlsm", ".csv"];

/** Une fenêtre d'aperçu : le contenu du fichier, relayé depuis notre origine
 *  (la politique de sécurité interdit d'encadrer OneDrive). PDF et images
 *  s'affichent ; le reste s'ouvre dans un onglet. */
function ApercuModale({
  url,
  nom,
  extension,
  octets,
  fermer,
}: {
  url: string;
  nom: string;
  extension: string;
  octets: number;
  fermer: () => void;
}) {
  const cadre = useModale<HTMLDivElement>(true, fermer);
  const trop = octets > APERCU_MAX_OCTETS;
  const affichable = !trop && TYPES_APERCU.includes(extension.toLowerCase());

  return (
    <div className="voile" role="dialog" aria-modal="true"
         aria-label={`Aperçu de ${nom}`}>
      <div className="modale modale--apercu" ref={cadre}>
        <div className="modale__tete">
          <h2 className="tronque" style={{ flex: 1, minWidth: 0 }}>{nom}</h2>
          <button type="button" className="action" title="Fermer"
                  aria-label="Fermer l'aperçu" onClick={fermer}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="modale__corps">
          {affichable ? (
            <iframe src={url} title={`Aperçu de ${nom}`} className="apercu__cadre" />
          ) : (
            <div className="pile" style={{ textAlign: "center",
                                           padding: "var(--mo-space-5)" }}>
              <span className="discret">
                {trop
                  ? "Fichier trop volumineux pour l'aperçu — importez-le pour l'ouvrir."
                  : "L'aperçu ne s'affiche pas pour ce type de fichier."}
              </span>
              {!trop && (
                <a className="bouton bouton--primaire" href={url}
                   target="_blank" rel="noreferrer"
                   style={{ alignSelf: "center" }}>
                  Ouvrir dans un onglet
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Une ligne de fichier dans l'éditeur : déplacer, écarter, étiqueter, commenter. */
function FichierLigne({
  fichier,
  carteClef,
  affaireId,
  patch,
  selectionnee,
  basculer,
  importer,
}: {
  fichier: FichierAudit;
  carteClef: string;
  affaireId: string;
  patch: (id: string, champs: Record<string, unknown>) => Promise<void>;
  /** Cochée pour l'import groupé (absent = carte non importable). */
  selectionnee?: boolean;
  basculer?: () => void;
  /** Importer ce seul fichier (absent = carte non importable). */
  importer?: () => Promise<void>;
}) {
  const [ouvert, poserOuvert] = useState(false);
  const [commentaire, poserCommentaire] = useState(fichier.commentaire);
  const [etiquettes, poserEtiquettes] = useState<string[]>(fichier.etiquettes);

  // Resynchroniser quand la LIGNE change de fichier, non à chaque recomposition :
  // les notes de fichier se persistent au fil de l'eau, l'état local est déjà bon.
  useEffect(() => {
    poserCommentaire(fichier.commentaire);
    poserEtiquettes(fichier.etiquettes);
  }, [fichier.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Les étiquettes d'un fichier se persistent à chaque changement (ajout/retrait
  // d'une puce est un geste discret) ; le commentaire, lui, à la perte du focus.
  const majEtiquettes = (tags: string[]) => {
    poserEtiquettes(tags);
    void patch(fichier.id, { etiquettes: tags });
  };

  const [apercu, poserApercu] = useState(false);

  // Vert quand importé, rouge tant qu'il ne l'est pas, ambre pendant la
  // descente. Seuls les fichiers importables (hors « AUTRE ») sont colorés.
  const etatClasse = !importer ? ""
    : fichier.importe === "PRET" ? " editeur__fichier--pret"
      : fichier.importe === "EN_COURS" ? " editeur__fichier--cours"
        : " editeur__fichier--rouge";

  return (
    <>
    <div className={`editeur__fichier${etatClasse}`}>
      <div className="rangee">
        {basculer && (
          <input type="checkbox" checked={selectionnee ?? false}
                 aria-label={`Sélectionner ${fichier.nom}`}
                 onChange={basculer} />
        )}
        <span aria-hidden="true">📄</span>
        <span className="tronque" style={{ flex: 1 }} title={fichier.motif}>
          {fichier.nom}
          {fichier.confiance === "faible" && !fichier.categorie_retenue && (
            <span className="discret"> · à confirmer</span>
          )}
        </span>
        {carteClef !== "AUTRE"
          && fichier.confiance === "faible" && !fichier.categorie_retenue && (
          <button type="button" className="action" title="Confirmer la catégorie"
                  aria-label={`Confirmer ${fichier.nom}`}
                  onClick={() => void patch(fichier.id,
                    { categorie_retenue: carteClef })}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="m5 12 5 5 9-11" />
            </svg>
          </button>
        )}
        <select aria-label={`Catégorie de ${fichier.nom}`}
                value={carteClef === "AUTRE" ? "" : carteClef}
                onChange={(e) => void patch(fichier.id,
                  { categorie_retenue: e.target.value || null })}>
          {carteClef === "AUTRE" && <option value="">— à classer —</option>}
          {CATEGORIES.map((c) => (
            <option key={c.clef} value={c.clef}>{c.libelle}</option>
          ))}
        </select>
        {importer && (
          fichier.importe === "PRET" ? (
            <span className="editeur__importok" title="Importé dans Documents">
              ✓ Importé
            </span>
          ) : fichier.importe === "EN_COURS" ? (
            <span className="discret" style={{ whiteSpace: "nowrap" }}>Import…</span>
          ) : (
            <button type="button"
                    className="bouton bouton--discret selecteur__choisir"
                    title="Importer dans Documents"
                    onClick={() => void importer()}>
              Importer
            </button>
          )
        )}
        <button type="button" className="action" title="Prévisualiser le contenu"
                aria-label={`Prévisualiser ${fichier.nom}`}
                onClick={() => poserApercu(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <button type="button" className="action"
                title="Écarter ce fichier" aria-label={`Écarter ${fichier.nom}`}
                onClick={() => void patch(fichier.id, { retire: true })}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
        <button type="button" className="action" aria-expanded={ouvert}
                title="Étiquettes et commentaire"
                onClick={() => poserOuvert((v) => !v)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M20 12H4M12 4v16" />
          </svg>
        </button>
      </div>
      {ouvert && (
        <div className="pile pile--serree editeur__fichier-notes">
          <div className="rangee" style={{ gap: "var(--mo-space-2)", alignItems: "flex-start" }}>
            <SaisieEtiquettes valeur={etiquettes} poser={majEtiquettes}
                              placeholder="Étiquettes"
                              ariaLabel={`Étiquettes de ${fichier.nom}`} />
            <BoutonSuggerer titre={`Proposer des étiquettes pour ${fichier.nom}`}
              url={`/api/v1/affaires/${affaireId}/audit/findings/${fichier.id}/suggestion?champ=etiquettes`}
              onSuggestion={(s) => {
                if (s.etiquettes) majEtiquettes(fusionnerTags(etiquettes, s.etiquettes));
              }} />
          </div>
          <div className="rangee" style={{ gap: "var(--mo-space-2)", alignItems: "flex-start" }}>
            <input value={commentaire} placeholder="Commentaire" style={{ flex: 1 }}
                   aria-label={`Commentaire sur ${fichier.nom}`}
                   onChange={(e) => poserCommentaire(e.target.value)}
                   onBlur={() => void patch(fichier.id, { commentaire })} />
            <BoutonSuggerer titre={`Proposer un commentaire pour ${fichier.nom}`}
              url={`/api/v1/affaires/${affaireId}/audit/findings/${fichier.id}/suggestion?champ=commentaire`}
              onSuggestion={(s) => {
                if (s.commentaire !== undefined) {
                  poserCommentaire(s.commentaire);
                  void patch(fichier.id, { commentaire: s.commentaire });
                }
              }} />
          </div>
        </div>
      )}
    </div>
    {apercu && (
      <ApercuModale nom={fichier.nom} extension={fichier.extension}
                    octets={fichier.octets}
                    url={`/api/v1/affaires/${affaireId}/audit/findings/${fichier.id}/apercu`}
                    fermer={() => poserApercu(false)} />
    )}
    </>
  );
}
